import { CopyOutlined } from '@ant-design/icons';
import { cx } from '@emotion/css';
import styled from '@emotion/styled';
import { Button, message, Space, Tag as ATag } from 'antd';
import { useIsStoreAliasFirst } from 'hooks/business/store/useStoreName';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import useTalkAvailable from 'hooks/business/useTalkAvailable';
import dayjs from 'lib/dayjs';
import { formatNumber } from 'utils/numberFormat';
import { HTMLAttributes, useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import { CancelOrder } from 'services/api/refund';
import { buyerNickMask } from 'services/channels/demo';
import copyText from 'utils/copyText';
import toLowerCase from 'utils/toLowerCase';
import { useGetReasonLabel } from 'hooks/business/refundReason/useGetReasonLabel';
import { lazadaServiceLinkMap } from '../utils/constants';
import { goPlatform } from '../utils/store';
import CancelApprovalBtns from './CancelApprovalBtns';
import { ContactBuyerBtnProps } from './ContactBuyerBtn';
import Product from './Product';
import { OrderRemark } from './OrderRemark';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { useGetRegionLabel } from 'hooks/business/useRegion';
import { BuyerInfo } from './ReturnListItem/BuyerInfo';

const itemTransformer = (item: CancelOrder['orderItemInfos'][0]) => {
  const targetItem = {
    itemImage: item.itemImages,
    returnQuantity: item.quantity,
    itemPrice: item.itemPrice,
    itemName: item.itemName,
    itemId: item.itemId,
    skuId: item.skuId,
    skuName: item.skuId,
  };
  return targetItem;
};

export const contactcRowTransformer = (raw: CancelOrder) => {
  const contactRow: ContactBuyerBtnProps['row'] = {
    orderSn: raw.orderId,
    buyerNick: buyerNickMask(raw.buyerName),
    region: toLowerCase(raw.region),
    externalStoreId: raw.externalStoreId,
    externalBuyerId: raw.buyerId,
    reverseOrderId: raw.reverseOrderId,
  };
  return contactRow;
};

interface Props extends HTMLAttributes<HTMLDivElement> {
  row: CancelOrder;
  platform: General.PlatformUpperCase;
  conversation?: Conversation.Conversation;
  refreshList: () => void;
}
const Container = styled.div`
  .goods-item {
    height: 96px;
    padding: 0;
    /* width: calc(100vw - 128px); */
    align-items: flex-start;
    &:hover {
      background-color: #f7f9fa;
      cursor: pointer;
    }
    td {
      padding-top: 17px;
    }
    .td-reason {
      > div {
        width: 100%;
        overflow: hidden;
        > div,
        .ant-tag {
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }
    .td-action {
      button {
        display: block;
        height: 20px;
        margin: 0;
        padding: 0;
        border: none;
        color: #515359;
        &.contact-lazada {
          color: #336fff;
        }
      }
    }
  }
`;

const OrderInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #f2f8ff;
  height: 42px;
  margin-top: 16px;
  padding: 0 16px;
  .anticon-copy {
    margin-left: 4px;
  }
  .storeName {
    width: 240px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .buyerNick {
    width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const CancelListItem = (props: Props) => {
  const { row, platform, refreshList, conversation, onClick, className, ...otherProps } = props;
  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();
  const { getReasonLabel } = useGetReasonLabel();
  const available = useTalkAvailable(conversation);
  const {
    region,
    storeName,
    orderId,
    reason,
    orderItemInfos: items,
    createTime,
    // textReason,
    orderState: returnStatus,
    unit,
    totalAmount: refundAmount,
    externalStoreId,
    // returnStatus,
    // returnUpdateTime,
    // externalBuyerId,
    storeAliasName: storeAlias,
    storeId,
    operate = [],
    internalTradeId,
    sellerMemos,
    lechatSellerMemos,
  } = row;
  const showAlias = useIsStoreAliasFirst(platform, region);
  const name = showAlias && storeAlias ? storeAlias : storeName;

  const operatable = useGetPermission()('操作弃单', { platform, region, storeId: Number(storeId) });

  const returnOrderLikeProduts = useMemo(() => items.map(itemTransformer), [items]);
  const serviceLink = lazadaServiceLinkMap[toLowerCase(region)];

  let opUI;
  if (operatable && platform === 'LAZADA' && serviceLink) {
    opUI = (
      <Button
        type="link"
        size="small"
        className="contact-lazada"
        onClick={() => goPlatform(storeId)}
      >
        {t('tableColumn.联系lazada客服')}
      </Button>
    );
  } else if (operatable && operate.length > 0 && conversation) {
    opUI = (
      <CancelApprovalBtns
        orderId={orderId}
        onUpdate={refreshList}
        platform={platform}
        region={region}
        internalStoreId={conversation.storeId}
        externalStoreId={externalStoreId}
        operate={operate}
        buttonProps={[
          {
            className: '!text-[#f05b5b]',
          },
          undefined,
        ]}
      />
    );
  }

  return (
    <Container className={cx('row', className)} {...otherProps}>
      <OrderInfo>
        <Space size="large">
          <span>{getRegionLabel(region)}</span>
          <span className="storeName">{name}</span>
          <span>
            {t('tableColumn.订单编号')}:{orderId}
            <CopyOutlined
              onClick={() => copyText(orderId).then(() => message.success(t('trade.复制成功')))}
            />
          </span>
        </Space>
        <Space size="large">
          {operatable && (
            <BuyerInfo
              row={contactcRowTransformer(row)}
              conversation={conversation}
              contactEnable
              type="cancel"
              onClose={refreshList}
            />
          )}
          {conversation?.receptionName ? <span>{conversation.receptionName}</span> : null}
        </Space>
      </OrderInfo>
      <tr key={items[0].itemId} className="goods-item" onClick={onClick}>
        <td className="td-title">
          <Product
            info={returnOrderLikeProduts}
            unit={unit}
            externalStoreId={externalStoreId}
            platform={platform}
            region={region}
          />
        </td>
        <td className="td-amount">
          {refundAmount ? unit : ''}
          {formatNumber(refundAmount, '0,0.00')}
        </td>
        <td className="td-reason">
          <div className="flex flex-col gap-1">
            <div>
              {/* @ts-ignore */}
              <ATag>{getReasonLabel(reason, platform)}</ATag>
            </div>
            <OrderRemark
              content={platform === 'SHOPEE' ? sellerMemos ?? '' : lechatSellerMemos ?? ''}
              internalTradeId={internalTradeId}
              platform={platform}
              onRefresh={refreshList}
              showEdit
            />
          </div>
        </td>
        <td className="td-status">
          <div>
            {t({
              key: isTiKTokUpperCase(platform)
                ? `tiktok.${returnStatus}`
                : `${toLowerCase(platform)}.${returnStatus}`,
              defaultValue: returnStatus,
            })}
          </div>
          <div>{dayjs(Number(createTime)).format('YYYY-MM-DD HH:mm:ss')}</div>
        </td>
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <td className="td-action" onClick={(e) => e.stopPropagation()}>
          {available ? (
            opUI
          ) : (
            <>
              {conversation?.receptionName
                ? t('fn.message.editor.tips.forwarded', {
                    name: conversation!.receptionName,
                  })
                : '-'}
            </>
          )}
          <Button type="link" size="small" onClick={onClick}>
            {t('tableColumn.查看详情')}
          </Button>
        </td>
      </tr>
    </Container>
  );
};
export default CancelListItem;
